import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getFulfillmentOptions } from "../components/cart/FulfillmentSelector";
import { useLocations } from "../hooks/useLocations";

const LocationContext = createContext(null);
const SELECTED_LOCATION_KEY = "zayas:selected-location";
const CART_KEY = "zayas:cart";
const FULFILLMENT_KEY = "zayas:fulfillment-type";

function readStoredJson(key, fallback) {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallback;
  } catch (error) {
    return fallback;
  }
}

function readStoredValue(key, fallback) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (error) {
    return fallback;
  }
}

function resolveFulfillmentType(location, currentType) {
  const options = getFulfillmentOptions(location);

  if (!options.length) {
    return "";
  }

  if (options.some((option) => option.value === currentType)) {
    return currentType;
  }

  return options[0].value;
}

function buildCartItem(menuItem, selectedLocation) {
  return {
    id: menuItem.id,
    menu_item_id: menuItem.id,
    name: menuItem.name,
    categoryName: menuItem.category?.name || "Favorite",
    image: menuItem.image || "",
    price: menuItem.price || menuItem.base_price || "0.00",
    quantity: 1,
    customer_note: "",
    locationSlug: selectedLocation.slug,
    locationName: selectedLocation.name,
  };
}

export function LocationProvider({ children }) {
  const { locations, loading, error } = useLocations();
  const [selectedLocation, setSelectedLocation] = useState(() => {
    return readStoredJson(SELECTED_LOCATION_KEY, null);
  });
  const [cartItems, setCartItems] = useState(() => readStoredJson(CART_KEY, []));
  const [fulfillmentType, setFulfillmentTypeState] = useState(() => {
    return readStoredValue(FULFILLMENT_KEY, "pickup");
  });

  useEffect(() => {
    if (!selectedLocation && locations.length === 1) {
      setSelectedLocation(locations[0]);
    }
  }, [locations, selectedLocation]);

  useEffect(() => {
    if (!selectedLocation) {
      return;
    }

    const freshLocation = locations.find((location) => location.slug === selectedLocation.slug);
    if (freshLocation && JSON.stringify(freshLocation) !== JSON.stringify(selectedLocation)) {
      setSelectedLocation(freshLocation);
    }
  }, [locations, selectedLocation]);

  useEffect(() => {
    if (selectedLocation) {
      localStorage.setItem(SELECTED_LOCATION_KEY, JSON.stringify(selectedLocation));
    }
  }, [selectedLocation]);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (!selectedLocation) {
      return;
    }

    setFulfillmentTypeState((currentType) => {
      return resolveFulfillmentType(selectedLocation, currentType);
    });
  }, [selectedLocation]);

  useEffect(() => {
    if (fulfillmentType) {
      localStorage.setItem(FULFILLMENT_KEY, fulfillmentType);
    }
  }, [fulfillmentType]);

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const setFulfillmentType = useCallback(
    (nextType) => {
      setFulfillmentTypeState(resolveFulfillmentType(selectedLocation, nextType));
    },
    [selectedLocation],
  );

  const chooseLocation = useCallback(
    (location, options = {}) => {
      const cartHasDifferentLocation = cartItems.some((item) => {
        return item.locationSlug !== location.slug;
      });

      if (cartHasDifferentLocation && !options.clearCart) {
        return {
          needsConfirmation: true,
          message: "Your cart has items from another location.",
        };
      }

      if (options.clearCart) {
        setCartItems([]);
      }

      setSelectedLocation(location);
      setFulfillmentTypeState((currentType) => resolveFulfillmentType(location, currentType));
      return { needsConfirmation: false };
    },
    [cartItems],
  );

  const addCartItem = useCallback(
    (menuItem) => {
      if (!selectedLocation || menuItem.sold_out) {
        return;
      }

      setCartItems((currentItems) => {
        const existingItem = currentItems.find((item) => {
          return item.id === menuItem.id && item.locationSlug === selectedLocation.slug;
        });

        if (existingItem) {
          return currentItems.map((item) => {
            if (item.id !== menuItem.id || item.locationSlug !== selectedLocation.slug) {
              return item;
            }

            return {
              ...item,
              quantity: Math.min(item.quantity + 1, 20),
            };
          });
        }

        return [...currentItems, buildCartItem(menuItem, selectedLocation)];
      });
    },
    [selectedLocation],
  );

  const updateCartItemQuantity = useCallback((itemId, quantity) => {
    const nextQuantity = Number(quantity);

    setCartItems((currentItems) => {
      if (nextQuantity < 1) {
        return currentItems.filter((item) => item.id !== itemId);
      }

      return currentItems.map((item) => {
        if (item.id !== itemId) {
          return item;
        }

        return {
          ...item,
          quantity: Math.min(nextQuantity, 20),
        };
      });
    });
  }, []);

  const updateCartItemNote = useCallback((itemId, customerNote) => {
    setCartItems((currentItems) => {
      return currentItems.map((item) => {
        if (item.id !== itemId) {
          return item;
        }

        return {
          ...item,
          customer_note: customerNote,
        };
      });
    });
  }, []);

  const removeCartItem = useCallback((itemId) => {
    setCartItems((currentItems) => currentItems.filter((item) => item.id !== itemId));
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const contextValue = useMemo(() => {
    return {
      locations,
      locationsLoading: loading,
      locationsError: error,
      selectedLocation,
      fulfillmentType,
      setFulfillmentType,
      chooseLocation,
      cartItems,
      cartCount,
      addCartItem,
      updateCartItemQuantity,
      updateCartItemNote,
      removeCartItem,
      clearCart,
    };
  }, [
    locations,
    loading,
    error,
    selectedLocation,
    fulfillmentType,
    setFulfillmentType,
    chooseLocation,
    cartItems,
    cartCount,
    addCartItem,
    updateCartItemQuantity,
    updateCartItemNote,
    removeCartItem,
    clearCart,
  ]);

  return <LocationContext.Provider value={contextValue}>{children}</LocationContext.Provider>;
}

export function useLocationSelection() {
  const context = useContext(LocationContext);

  if (!context) {
    throw new Error("useLocationSelection must be used within LocationProvider");
  }

  return context;
}
